// src/pages/JourneyMap/phases/Step4Boost.jsx
// Passo 4 — TURBINAR: seleciona plano, gera PIX, confirma pagamento
import React, { useState } from 'react';
import EdyAssistant from '../components/EdyAssistant';
import { BanknotesIcon } from '@heroicons/react/24/outline';
import apiClient from '../../../lib/apiClient';

const PLANS = [
  {
    id: 'starter',
    label: 'Starter',
    price: 97,
    color: '#22d3ee',
    reach: '3k–8k',
    features: ['Alcance inicial', '7 dias de veiculação', 'Relatório básico'],
  },
  {
    id: 'pro',
    label: 'Pro',
    price: 197,
    color: '#a855f7',
    reach: '15k–40k',
    features: ['Alcance ampliado', '14 dias de veiculação', 'Relatório completo', 'Público segmentado'],
    highlight: true,
  },
  {
    id: 'unlimited',
    label: 'Unlimited',
    price: 397,
    color: '#f97316',
    reach: '50k–120k',
    features: ['Alcance máximo', '30 dias de veiculação', 'Relatório premium', 'A/B automático', 'Suporte prioritário'],
  },
];

export default function Step4Boost({ campaignId, onAdvance }) {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [pixCode,      setPixCode]      = useState('');
  const [checkoutId,   setCheckoutId]   = useState('');
  const [showPix,      setShowPix]      = useState(false);
  const [loading,      setLoading]      = useState(false);
  const [confirming,   setConfirming]   = useState(false);
  const [copied,       setCopied]       = useState(false);
  const [error,        setError]        = useState('');

  // Edy messages
  const edyMsg = showPix
    ? 'Quase lá! Escaneie o QR code ou copie a chave PIX para ativar seus anúncios'
    : selectedPlan
    ? `Ótima escolha! O plano ${selectedPlan.label} vai fazer seus vendas decolarem!`
    : 'Escolha o plano ideal e veja seus anúncios voarem';
  const edyMood = showPix ? 'rocket' : selectedPlan ? 'joinha' : 'rocket';

  const handleSelectPlan = async (plan) => {
    setSelectedPlan(plan);
    setError('');
    setLoading(true);
    try {
      const r = await apiClient.request(`/mp/journey/${campaignId}/boost`, {
        method: 'POST',
        body:   JSON.stringify({ plan_id: plan.id, amount: plan.price }),
      });
      const d = await r.json();
      if (d.success) {
        setPixCode(d.pix_code || d.checkout?.pix_code || 'PIX_EDY_DEMO_00000000');
        setCheckoutId(d.checkout_id || d.checkout?.id || '');
        setShowPix(true);
      } else {
        setError(d.message || 'Erro ao gerar pagamento.');
      }
    } catch {
      setError('Erro de conexão ao criar pagamento.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(pixCode).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleConfirmPayment = async () => {
    if (!checkoutId) { setError('ID do checkout não encontrado.'); return; }
    setConfirming(true);
    setError('');
    try {
      const r = await apiClient.request('/mp/journey/webhook', {
        method: 'POST',
        body:   JSON.stringify({ checkout_id: checkoutId, status: 'paid' }),
      });
      const d = await r.json();
      if (d.success) {
        onAdvance(d.campaign);
      } else {
        setError(d.message || 'Erro ao confirmar pagamento.');
      }
    } catch {
      setError('Erro de conexão ao confirmar pagamento.');
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div className="jm-container">
      <EdyAssistant message={edyMsg} mood={edyMood} loading={loading} />

      {/* Tela de planos */}
      {!showPix && (
        <div className="jm-plans">
          {PLANS.map(plan => (
            <button
              key={plan.id}
              className={`jm-plan-card${plan.highlight ? ' highlight' : ''}${selectedPlan?.id === plan.id ? ' selected' : ''}`}
              onClick={() => !loading && handleSelectPlan(plan)}
              disabled={loading}
              style={{ '--plan-color': plan.color }}
            >
              {plan.highlight && <span className="jm-plan-badge">MAIS POPULAR</span>}
              <div className="jm-plan-name">{plan.label}</div>
              <div className="jm-plan-price">
                <span className="jm-price-currency">R$</span>
                <span className="jm-price-amount">{plan.price}</span>
                <span className="jm-price-period">/campanha</span>
              </div>
              <div className="jm-plan-reach">Alcance estimado: <strong>{plan.reach} pessoas</strong></div>
              <ul className="jm-plan-features">
                {plan.features.map(f => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
              {loading && selectedPlan?.id === plan.id && (
                <span className="jm-plan-loading">Gerando PIX...</span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Modal / card PIX */}
      {showPix && selectedPlan && (
        <div className="jm-pix-modal">
          <div className="jm-pix-header">
            <span className="jm-pix-icon"><BanknotesIcon style={{width:24,height:24}} /></span>
            <h3>Pagamento PIX</h3>
            <p>Plano {selectedPlan.label} — <strong>R${selectedPlan.price}</strong></p>
          </div>

          {/* QR code simulado via placeholder */}
          <div className="jm-pix-qr">
            <img
              src={`https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(pixCode)}&size=180x180&bgcolor=0d0d1a&color=22d3ee`}
              alt="QR Code PIX"
              width={180}
              height={180}
              onError={e => { e.target.style.display = 'none'; }}
            />
          </div>

          <div className="jm-pix-code-wrap">
            <code className="jm-pix-code">{pixCode}</code>
            <button className="jm-btn-ghost jm-btn-copy" onClick={handleCopy}>
              {copied ? 'Copiado!' : 'Copiar'}
            </button>
          </div>

          {error && <p style={{ color: 'var(--edy-red)', fontSize: '0.82rem', textAlign: 'center', marginTop: 8 }}>{error}</p>}

          <button
            className="jm-btn-cta jm-btn-full"
            onClick={handleConfirmPayment}
            disabled={confirming}
            style={{ marginTop: 18 }}
          >
            {confirming ? 'Verificando pagamento...' : 'Já paguei — Ativar anúncios!'}
          </button>

          <button
            className="jm-btn-ghost jm-btn-full"
            onClick={() => { setShowPix(false); setSelectedPlan(null); setError(''); }}
            style={{ marginTop: 8 }}
          >
            ← Escolher outro plano
          </button>
        </div>
      )}
    </div>
  );
}
